import {memo, useCallback, useMemo} from 'react';
import type {ReactElement, ReactNode} from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type {PieLabelRenderProps} from 'recharts';
import type {CanvasChartBlock, CanvasChartSeries} from '../types';
import {BlockLayout} from '../block-layout';
import {MarkdownRenderer} from '../../markdown-renderer';
import type {BlockRendererProps} from './types';

const FALLBACK_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-6))',
];

/**
 * Brand-first color palette: uses canvas brand primary at full and reduced
 * opacities before falling back to the standard chart palette.
 */
const BRAND_COLORS = [
  'var(--canvas-primary)',
  'color-mix(in srgb, var(--canvas-primary) 65%, transparent)',
  'color-mix(in srgb, var(--canvas-primary) 40%, transparent)',
  ...FALLBACK_COLORS,
];

function getSeriesColor(series: CanvasChartSeries, index: number): string {
  return series.color ?? BRAND_COLORS[index % BRAND_COLORS.length];
}

function formatCompactNumber(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return value.toLocaleString();
}

const CHART_MARGIN = {top: 8, right: 12, bottom: 8, left: 0} as const;
const AXIS_TICK = {
  fontSize: 11,
  fill: 'hsl(var(--muted-foreground))',
} as const;
const TOOLTIP_CURSOR = {
  fill: 'hsl(var(--accent))',
  strokeDasharray: '3 3',
} as const;
const ACTIVE_DOT = {
  r: 4,
  strokeWidth: 2,
  fill: 'hsl(var(--background))',
} as const;
const PIE_LABEL_LINE = {stroke: 'hsl(var(--border))', strokeWidth: 1} as const;

interface ChartContentProps {
  block: CanvasChartBlock;
  rechartsData: Record<string, unknown>[];
  formattedMap: Map<string, Record<string, string>>;
}

const CustomTooltip = ({
  active,
  payload,
  label,
  formattedMap,
  isPie = false,
}: {
  active?: boolean;
  payload?: Array<{
    dataKey: string;
    value: number;
    color: string;
    name?: string;
  }>;
  label?: string;
  formattedMap: Map<string, Record<string, string>>;
  isPie?: boolean;
}) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="rounded-lg border border-border bg-background/95 px-3 py-2.5 shadow-lg backdrop-blur-xs">
      {!isPie && label && (
        <p className="mb-1.5 text-xs font-medium text-foreground">{label}</p>
      )}
      <div className="space-y-1">
        {payload.map((entry) => {
          const entryLabel = isPie
            ? (entry.name ?? entry.dataKey)
            : entry.dataKey;
          const lookupKey = isPie ? entry.name : label;
          const formatted = lookupKey ? formattedMap.get(lookupKey) : undefined;

          return (
            <div key={entryLabel} className="flex items-center gap-2 text-xs">
              <span
                className="size-2 rounded-full"
                style={{backgroundColor: entry.color}}
              />
              <span className="text-muted-foreground">{entryLabel}</span>
              <span className="ml-auto font-semibold tabular-nums text-foreground">
                {formatted?.[entry.dataKey] ?? formatCompactNumber(entry.value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CustomLegend = ({
  payload,
}: {
  payload?: Array<{value: string; color: string}>;
}) => {
  if (!payload?.length) {
    return null;
  }

  return (
    <div className="flex items-center justify-center gap-5 pb-1 pt-4">
      {payload.map((entry) => (
        <div key={entry.value} className="flex items-center gap-1.5">
          <span
            className="size-2.5 rounded-sm"
            style={{backgroundColor: entry.color}}
          />
          <span className="text-xs text-muted-foreground">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

const LEGEND_CONTENT = <CustomLegend />;

const CartesianChart = ({
  block: _block,
  rechartsData,
  formattedMap,
  children,
  ChartComponent,
}: ChartContentProps & {
  ChartComponent: typeof LineChart | typeof BarChart | typeof AreaChart;
  children: ReactNode;
}) => {
  const tooltipContent = useMemo(
    () => <CustomTooltip formattedMap={formattedMap} />,
    [formattedMap],
  );

  return (
    <ChartComponent data={rechartsData} margin={CHART_MARGIN}>
      <CartesianGrid
        strokeDasharray="none"
        vertical={false}
        className="stroke-border/40"
      />
      <XAxis
        dataKey="name"
        axisLine={false}
        tickLine={false}
        tick={AXIS_TICK}
        tickMargin={12}
      />
      <YAxis
        axisLine={false}
        tickLine={false}
        tick={AXIS_TICK}
        tickMargin={4}
        tickFormatter={formatCompactNumber}
        width={52}
      />
      <Tooltip
        cursor={TOOLTIP_CURSOR}
        content={tooltipContent}
        isAnimationActive={false}
      />
      <Legend content={LEGEND_CONTENT} />
      {children}
    </ChartComponent>
  );
};

const LineChartContent = (props: ChartContentProps) => (
  <CartesianChart {...props} ChartComponent={LineChart}>
    {props.block.series.map((s, i) => (
      <Line
        key={s.key}
        type="monotone"
        dataKey={s.key}
        stroke={getSeriesColor(s, i)}
        strokeWidth={2}
        dot={false}
        activeDot={ACTIVE_DOT}
        isAnimationActive={false}
      />
    ))}
  </CartesianChart>
);

const BarChartContent = (props: ChartContentProps) => (
  <CartesianChart {...props} ChartComponent={BarChart}>
    {props.block.series.map((s, i) => (
      <Bar
        key={s.key}
        dataKey={s.key}
        fill={getSeriesColor(s, i)}
        radius={[6, 6, 0, 0]}
        maxBarSize={56}
        isAnimationActive={false}
      />
    ))}
  </CartesianChart>
);

const StackedBarChartContent = (props: ChartContentProps) => (
  <CartesianChart {...props} ChartComponent={BarChart}>
    {props.block.series.map((s, i) => (
      <Bar
        key={s.key}
        dataKey={s.key}
        fill={getSeriesColor(s, i)}
        stackId="a"
        radius={i === props.block.series.length - 1 ? [6, 6, 0, 0] : undefined}
        maxBarSize={56}
        isAnimationActive={false}
      />
    ))}
  </CartesianChart>
);

const AreaChartContent = (props: ChartContentProps) => (
  <CartesianChart {...props} ChartComponent={AreaChart}>
    {props.block.series.map((s, i) => {
      const color = getSeriesColor(s, i);
      return (
        <Area
          key={s.key}
          type="monotone"
          dataKey={s.key}
          stroke={color}
          fill={color}
          fillOpacity={0.1}
          strokeWidth={2}
          dot={false}
          activeDot={ACTIVE_DOT}
          isAnimationActive={false}
        />
      );
    })}
  </CartesianChart>
);

const RADIAN = Math.PI / 180;
const MAX_PIE_LABEL_LENGTH = 20;

const renderPieLabel = (
  props: PieLabelRenderProps,
  dataKey: string,
  formattedMap: Map<string, Record<string, string>>,
) => {
  const cx = props.cx ?? 0;
  const cy = props.cy ?? 0;
  const midAngle = props.midAngle ?? 0;
  const outerRadius = props.outerRadius ?? 0;
  const name = props.name ?? '';
  const value = props.value;

  const radius = outerRadius + 20;

  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  const formatted = name ? formattedMap.get(name)?.[dataKey] : undefined;
  const displayName =
    name.length > MAX_PIE_LABEL_LENGTH
      ? `${name.slice(0, MAX_PIE_LABEL_LENGTH).trimEnd()}…`
      : name;

  return (
    <text
      x={x}
      y={y}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="fill-muted-foreground text-[11px]"
    >
      {displayName}
      <tspan className="fill-foreground font-medium">
        {' '}
        {formatted ?? value}
      </tspan>
    </text>
  );
};

const PieChartContent = ({
  block,
  rechartsData,
  formattedMap,
}: ChartContentProps) => {
  const firstRow = rechartsData[0];
  const dataKey = firstRow
    ? (Object.keys(firstRow).find((k) => k !== 'name') ?? 'value')
    : 'value';

  const coloredData = useMemo(
    () =>
      rechartsData.map((entry, index) => ({
        ...entry,
        fill: getSeriesColor(
          block.series[index] ?? {key: dataKey, color: null},
          index,
        ),
      })),
    [rechartsData, block.series, dataKey],
  );

  const labelFn = useCallback(
    (props: PieLabelRenderProps) =>
      renderPieLabel(props, dataKey, formattedMap),
    [dataKey, formattedMap],
  );

  const tooltipContent = useMemo(
    () => <CustomTooltip formattedMap={formattedMap} isPie />,
    [formattedMap],
  );

  return (
    <PieChart>
      <Pie
        data={coloredData}
        dataKey={dataKey}
        nameKey="name"
        cx="50%"
        cy="50%"
        outerRadius="70%"
        innerRadius={block.chartType === 'donut' ? '45%' : 0}
        paddingAngle={2}
        strokeWidth={0}
        isAnimationActive={false}
        label={labelFn}
        labelLine={PIE_LABEL_LINE}
      />
      <Tooltip content={tooltipContent} isAnimationActive={false} />
    </PieChart>
  );
};

const CHART_RENDERERS: Partial<
  Record<string, (props: ChartContentProps) => ReactElement>
> = {
  line: LineChartContent,
  bar: BarChartContent,
  stacked_bar: StackedBarChartContent,
  area: AreaChartContent,
  pie: PieChartContent,
  donut: PieChartContent,
};

const AxisLabels = ({
  xAxisLabel,
  yAxisLabel,
}: {
  xAxisLabel?: string | null;
  yAxisLabel?: string | null;
}) => {
  if (!xAxisLabel && !yAxisLabel) {
    return null;
  }
  return (
    <div className="flex items-center justify-between px-1 pb-2 pt-0.5">
      {yAxisLabel ? (
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
          {yAxisLabel}
        </span>
      ) : (
        <span />
      )}
      {xAxisLabel ? (
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
          {xAxisLabel}
        </span>
      ) : (
        <span />
      )}
    </div>
  );
};

export const ChartBlock = memo(
  ({block}: BlockRendererProps<CanvasChartBlock>) => {
    const rechartsData = useMemo(
      () => block.data.map((row) => ({name: row.name, ...row.values})),
      [block.data],
    );

    const formattedMap = useMemo(
      () =>
        new Map(
          block.data
            .filter((row) => row.formatted)
            .map((row) => [row.name, row.formatted!]),
        ),
      [block.data],
    );

    const Renderer = CHART_RENDERERS[block.chartType];
    if (!Renderer) {
      return null;
    }

    return (
      <BlockLayout title={block.title}>
        <div className="overflow-hidden rounded-xl border border-border bg-background px-2 pb-2 pt-4 shadow-xs">
          <AxisLabels
            xAxisLabel={block.xAxisLabel}
            yAxisLabel={block.yAxisLabel}
          />
          <div className="h-72 [&_*:focus]:outline-hidden">
            <ResponsiveContainer width="100%" height="100%">
              <Renderer
                block={block}
                rechartsData={rechartsData}
                formattedMap={formattedMap}
              />
            </ResponsiveContainer>
          </div>
        </div>
        {block.description && (
          <MarkdownRenderer
            content={block.description}
            className="mt-3 max-w-none text-sm text-muted-foreground"
          />
        )}
      </BlockLayout>
    );
  },
);
ChartBlock.displayName = 'ChartBlock';

export const chartBlockConfig = {
  component: ChartBlock,
  isEmpty: (block: CanvasChartBlock) => block.data.length === 0,
};
